import { useEffect, useState } from 'react'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import LoadingSpinner from '@/components/LoadingSpinner'
import toast from 'react-hot-toast'
import { voucherProgramService } from '@/services/voucherProgramService'
import { voucherService } from '@/services/voucherService'
import { useBranch } from '@/contexts/BranchContext'
import { washCatalogService } from '@/services/washCatalogService'

function formatMoney(n) {
  return `R${Number(n || 0).toFixed(2)}`
}

export default function VouchersPage() {
  const { currentBranch, isViewingAllBranches } = useBranch()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [program, setProgram] = useState(null)
  const [recent, setRecent] = useState([])
  const [washCatalog, setWashCatalog] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const [p, catalog] = await Promise.all([voucherProgramService.get(), washCatalogService.get()])
        setWashCatalog(catalog)
        if (cancelled) return
        setProgram({
          enabled: Boolean(p.enabled),
          spendThreshold: Number(p.spendThreshold || 900),
          rewardAmount: Number(p.rewardAmount || 100),
          rewardLabel: p.rewardLabel || 'Voucher',
          rewardKind: p.rewardKind || p.rewardType || 'amount',
          rewardCatalogServiceId: p.rewardCatalogServiceId || null,
          rewardCatalogTierId: p.rewardCatalogTierId || null,
        })

        const list = await voucherService.listVouchers(
          isViewingAllBranches() ? {} : { branchId: currentBranch?.id },
          50
        )
        if (!cancelled) setRecent(list)
      } catch (e) {
        console.error(e)
        toast.error('Failed to load voucher settings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [currentBranch?.id, isViewingAllBranches])

  async function save() {
    if (!program) return
    if (!program.spendThreshold || program.spendThreshold <= 0) {
      toast.error('Spend threshold must be greater than 0')
      return
    }
    if (program.rewardKind === 'service' && (!program.rewardCatalogServiceId || !program.rewardCatalogTierId)) {
      toast.error('Select which wash + vehicle type the voucher gives for free')
      return
    }
    setSaving(true)
    try {
      await voucherProgramService.update({
        enabled: program.enabled,
        spendThreshold: Number(program.spendThreshold),
        rewardAmount: Number(program.rewardAmount),
        rewardLabel: program.rewardLabel || 'Voucher',
        rewardKind: program.rewardKind || 'amount',
        rewardCatalogServiceId: program.rewardKind === 'service' ? program.rewardCatalogServiceId : null,
        rewardCatalogTierId: program.rewardKind === 'service' ? program.rewardCatalogTierId : null,
      })
      toast.success('Voucher program updated')
    } catch (e) {
      console.error(e)
      toast.error('Failed to save voucher program')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <PageHeader
        eyebrow="Loyalty"
        title="Vouchers"
        description="Configure how vouchers are earned and review recent vouchers."
      />

      <Card className="shadow-sm border-border/80">
        <CardHeader>
          <CardTitle className="text-base">Voucher program</CardTitle>
          <CardDescription>Dynamic rules for spend-to-earn vouchers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading || !program ? (
            <div className="flex h-24 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Spend threshold (R)</Label>
                  <Input
                    inputMode="decimal"
                    value={program.spendThreshold}
                    onChange={(e) => setProgram((p) => ({ ...p, spendThreshold: Number(e.target.value || 0) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Voucher reward</Label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={program.rewardKind || 'amount'}
                    onChange={(e) => {
                      const kind = e.target.value
                      setProgram((p) => ({
                        ...p,
                        rewardKind: kind,
                        rewardCatalogServiceId: kind === 'service' ? p.rewardCatalogServiceId : null,
                        rewardCatalogTierId: kind === 'service' ? p.rewardCatalogTierId : null,
                      }))
                    }}
                  >
                    <option value="amount">Money voucher (Rand value)</option>
                    <option value="service">Free wash service voucher</option>
                  </select>
                </div>
              </div>

              {program.rewardKind === 'amount' ? (
                <div className="space-y-2">
                  <Label>Reward value (R)</Label>
                  <Input
                    inputMode="decimal"
                    value={program.rewardAmount}
                    onChange={(e) => setProgram((p) => ({ ...p, rewardAmount: Number(e.target.value || 0) }))}
                  />
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Free service</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={program.rewardCatalogServiceId || ''}
                      onChange={(e) => {
                        const svcId = e.target.value || null
                        setProgram((p) => ({
                          ...p,
                          rewardCatalogServiceId: svcId,
                          rewardCatalogTierId: null,
                        }))
                      }}
                    >
                      <option value="">Select a wash service…</option>
                      {(washCatalog?.packages || []).map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Vehicle type / tier</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={program.rewardCatalogTierId || ''}
                      onChange={(e) => setProgram((p) => ({ ...p, rewardCatalogTierId: e.target.value || null }))}
                      disabled={!program.rewardCatalogServiceId}
                    >
                      <option value="">Select a tier…</option>
                      {(
                        (washCatalog?.packages || []).find((x) => x.id === program.rewardCatalogServiceId)?.tiers || []
                      ).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label} (R{Number(t.price || 0).toFixed(2)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reward label</Label>
                <Input
                  value={program.rewardLabel}
                  onChange={(e) => setProgram((p) => ({ ...p, rewardLabel: e.target.value }))}
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save voucher program'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Example: Spend R{program.spendThreshold.toFixed(2)} earns{' '}
                {program.rewardKind === 'service'
                  ? `1 free wash voucher`
                  : `a ${program.rewardLabel} worth ${formatMoney(program.rewardAmount)}`}.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm border-border/80">
        <CardHeader>
          <CardTitle className="text-base">Recent vouchers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <div className="flex h-24 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No vouchers yet.</p>
          ) : (
            recent.map((v) => (
              <div
                key={v.id}
                className="flex flex-col gap-1 rounded-lg border border-border/80 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {v.customerName || v.customerId} · {formatMoney(v.value)}
                  </p>
                  <p className="text-xs text-muted-foreground">Status: {v.status}</p>
                </div>
                <Badge variant={v.status === 'redeemed' ? 'secondary' : 'default'}>
                  {v.status === 'redeemed' ? 'Redeemed' : 'Active'}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  )
}

